import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ScoreLeague = {
  id: string;
  league_code: string;
  display_name: string;
  sport: string;
  coverage_status: string;
};

export type ScoreEvent = {
  id: string;
  competition_id: string;
  league: string;
  leagueCode: string;
  status: string;
  statusDetail: string | null;
  period: string | null;
  startTime: string;
  venue: string | null;
  home: { name: string; logo: string | null };
  away: { name: string; logo: string | null };
  homeScore: number | null;
  awayScore: number | null;
  broadcastUrl: string | null;
  sourceUrl: string | null;
  lastUpdatedAt: string;
};

export type Scoreboard = {
  leagues: ScoreLeague[];
  events: ScoreEvent[];
  lastUpdatedAt: string | null;
};

/** Public scoreboard: RLS returns only published events of visible, available leagues. */
export function useScoreboard() {
  return useQuery({
    queryKey: ["scoreboard"],
    queryFn: async (): Promise<Scoreboard> => {
      const since = new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString();
      const [leaguesRes, eventsRes] = await Promise.all([
        supabase
          .from("score_leagues")
          .select("id, league_code, display_name, sport, coverage_status")
          .order("sort_order"),
        supabase
          .from("score_events")
          .select(
            "id, competition_id, status, status_detail, period, start_time, venue, home_name, away_name, home_logo_url, away_logo_url, home_score, away_score, broadcast_url, source_url, last_updated_at",
          )
          .gte("start_time", since)
          .order("start_time", { ascending: true })
          .limit(200),
      ]);
      if (leaguesRes.error) throw leaguesRes.error;
      if (eventsRes.error) throw eventsRes.error;

      const leagues = (leaguesRes.data ?? []).filter(
        (l) => l.coverage_status !== "unavailable",
      ) as ScoreLeague[];
      const byId = new Map(leagues.map((l) => [l.id, l]));

      const events: ScoreEvent[] = (eventsRes.data ?? [])
        .filter((e) => byId.has(e.competition_id))
        .map((e) => {
          const league = byId.get(e.competition_id)!;
          return {
            id: e.id,
            competition_id: e.competition_id,
            league: league.display_name,
            leagueCode: league.league_code,
            status: e.status,
            statusDetail: e.status_detail,
            period: e.period,
            startTime: e.start_time,
            venue: e.venue,
            home: { name: e.home_name, logo: e.home_logo_url },
            away: { name: e.away_name, logo: e.away_logo_url },
            homeScore: e.home_score,
            awayScore: e.away_score,
            broadcastUrl: e.broadcast_url,
            sourceUrl: e.source_url,
            lastUpdatedAt: e.last_updated_at,
          };
        });

      const lastUpdatedAt =
        events.length > 0
          ? events.reduce((a, b) => (a.lastUpdatedAt > b.lastUpdatedAt ? a : b)).lastUpdatedAt
          : null;

      return { leagues, events, lastUpdatedAt };
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

const DAY = 1000 * 60 * 60 * 24;

export function bucketEvents(events: ScoreEvent[]) {
  const now = Date.now();
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);

  const live = events.filter((e) => e.status === "live");
  const rest = events.filter((e) => e.status !== "live");
  const scheduled = rest.filter(
    (e) => e.status === "scheduled" || e.status === "postponed",
  );
  const today = scheduled.filter((e) => {
    const t = new Date(e.startTime).getTime();
    return t >= now && t <= endOfToday.getTime();
  });
  const upcoming = scheduled.filter((e) => new Date(e.startTime).getTime() > endOfToday.getTime());
  const recent = rest
    .filter(
      (e) =>
        (e.status === "final" || e.status === "cancelled" || e.status === "canceled") &&
        new Date(e.startTime).getTime() > now - 7 * DAY,
    )
    .sort((a, b) => (a.startTime < b.startTime ? 1 : -1));

  return { live, today, upcoming, recent };
}
